import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MarkdownEditor } from "@/components/shared/MarkdownEditor";
import type { ProductBuilderHeader } from "./use-product-builder-state";

interface Props {
  header: ProductBuilderHeader;
  onChange: (patch: Partial<ProductBuilderHeader>) => void;
  categorySlugs: string[];
}

export function ProductBuilderHeaderForm({
  header,
  onChange,
  categorySlugs,
}: Props): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t("platformEnterprises.productFieldName")}</Label>
          <Input
            value={header.displayName}
            onChange={(e) => onChange({ displayName: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("platformEnterprises.productFieldCategory")}</Label>
          {categorySlugs.length > 0 ? (
            <Select
              value={header.category}
              onValueChange={(v) => onChange({ category: v })}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("platformEnterprises.productFieldCategoryPlaceholder")}
                />
              </SelectTrigger>
              <SelectContent>
                {categorySlugs.map((slug) => (
                  <SelectItem key={slug} value={slug}>
                    {slug}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={header.category}
              onChange={(e) => onChange({ category: e.target.value })}
              placeholder={t("platformEnterprises.productFieldCategoryPlaceholder")}
            />
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>{t("platformEnterprises.productFieldDescription")}</Label>
        <MarkdownEditor
          value={header.description}
          onChange={(v) => onChange({ description: v })}
          rows={4}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t("platformEnterprises.productFieldSortOrder")}</Label>
          <Input
            type="number"
            value={header.sortOrder}
            onChange={(e) => onChange({ sortOrder: Number(e.target.value) })}
          />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Switch
            checked={header.isActive}
            onCheckedChange={(v) => onChange({ isActive: v })}
          />
          <Label>{t("platformEnterprises.productFieldActive")}</Label>
        </div>
      </div>
    </div>
  );
}
